import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import Joyride, { CallBackProps, STATUS, EVENTS, ACTIONS, Step } from 'react-joyride';
import { markOnboardingComplete } from '../utils/onboarding';

// A tour step plus the route its target lives on. Steps with no `route` target
// elements present on every page (the header), so they need no navigation.
interface TourStep extends Step {
  route?: string;
}

// Cross-route spotlight tour. Each step highlights a real element via a
// `data-tour="…"` selector and shows a tip popover; advancing walks the user
// through the whole app — and the key buttons on each page — navigating
// page-to-page as needed. Multiple steps can share a `route`; only the first
// one entering that route triggers navigation, the rest just advance.
const TOUR_STEPS: TourStep[] = [
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Welcome to FS App Manager 👋',
    content:
      "Quick guided tour — read each tip and click Next to move the highlight to the next control. It takes a minute or two, and you'll only see it once. Replay anytime from Help or ⌘/Ctrl+K.",
  },
  {
    target: '[data-tour="main-nav"]',
    placement: 'auto',
    disableBeacon: true,
    title: 'Top navigation',
    content: 'Jump between sections here — Deployment, App Center, Build Info, and Help. We’ll walk through each page’s key buttons next.',
  },
  {
    target: '[data-tour="quick-search"]',
    placement: 'auto',
    disableBeacon: true,
    title: 'Quick search (⌘/Ctrl + K)',
    content: 'Open the command palette from anywhere to jump to a page, find an app, or run an action — including replaying this tour.',
  },

  // ---- Home ----
  {
    route: '/home',
    target: '[data-tour="page-home"]',
    placement: 'center',
    disableBeacon: true,
    title: 'Dashboard Home',
    content: 'Your landing page: a personalized greeting, your pinned watch list, and the recent build-activity feed at a glance.',
  },
  {
    route: '/home',
    target: '[data-tour="home-feed"]',
    placement: 'auto',
    disableBeacon: true,
    title: 'Recent Activity Feed',
    content:
      'Filter the feed by All / Failed / Running / My builds / Last hour. Click any row to open a side panel with the build’s core failure reason, its phase timeline, and the complete GitLab job log — split per job — without leaving Home.',
  },

  // ---- App Deployment ----
  {
    route: '/home/deployment',
    target: '[data-tour="deploy-button"]',
    placement: 'auto',
    disableBeacon: true,
    title: 'Deploy a build',
    content:
      'Select one or more apps and a version, then click Deploy to trigger iOS/Android builds. Use “Patch update” to auto-bump the patch, “Select all apps” to batch across the whole filtered list, or “Unselect” to clear the selection in one click.',
  },
  {
    route: '/home/deployment',
    target: '[data-tour="deploy-version-filter"]',
    placement: 'auto',
    disableBeacon: true,
    title: 'Filter apps by version',
    content:
      'Pick a version from this dropdown to narrow the table to just the apps on that release — handy for shipping a follow-up to everything on a specific version. It filters the whole fleet, not just the current page; click the × to clear it.',
  },
  {
    route: '/home/deployment',
    target: '[data-tour="deploy-table"]',
    placement: 'auto',
    disableBeacon: true,
    title: 'Rows, details & store links',
    content: 'The checkbox selects an app for deploy — clicking anywhere else on a row opens its details (composer host, site key, bundle id, store listing). The Store column links straight to the public App Store / Play Store listing.',
  },
  {
    route: '/home/deployment',
    target: '[data-tour="notifications-bell"]',
    placement: 'auto',
    disableBeacon: true,
    title: 'Build notifications',
    content: 'Toggle browser notifications so you get pinged when the builds you trigger finish. (Keep this tab open to receive them.)',
  },

  // ---- Deploy Scheduler ----
  {
    route: '/home/deploy-scheduler',
    target: '[data-tour="new-schedule"]',
    placement: 'auto',
    disableBeacon: true,
    title: 'Schedule a deploy',
    content: 'Queue a deployment to fire at a chosen time. Scheduled deploys notify you when they run, no matter which page you’re on.',
  },

  // ---- App Distribution ----
  {
    route: '/home/app-distribution',
    target: '[data-tour="distribution-export"]',
    placement: 'auto',
    disableBeacon: true,
    title: 'Export distribution data',
    content: 'Download the data as CSV, or export the whole page as a PNG for reporting. Use "Show / hide cards" to choose which charts appear.',
  },

  // ---- Config Readiness ----
  {
    route: '/home/apps',
    target: '[data-tour="apps-filter"]',
    placement: 'auto',
    disableBeacon: true,
    title: 'Spot config gaps',
    content: 'Config Readiness flags apps missing a site key or a bundle identifier. Filter to "Needs attention" to see what to fix before deploying.',
  },

  // ---- Build Browser ----
  {
    route: '/home/builds',
    target: '[data-tour="builds-filter"]',
    placement: 'auto',
    disableBeacon: true,
    title: 'Filter builds by status',
    content: 'Browse the full build history and filter by All / Success / Failed / Running. Each row links out to its GitLab pipeline.',
  },

  // ---- Build Analytics ----
  {
    route: '/home/build-analytics',
    target: '[data-tour="analytics-refresh"]',
    placement: 'auto',
    disableBeacon: true,
    title: 'Refresh analytics',
    content: 'Pull the latest full build dataset for the charts — success rates, volume, and durations. The heavy refresh only runs while this page is open.',
  },

  // ---- Build Duration ----
  {
    route: '/home/build-cost',
    target: '[data-tour="cost-window"]',
    placement: 'auto',
    disableBeacon: true,
    title: 'Pick a time window',
    content: 'Switch between 7, 30, and 90 days to see build durations and the top apps by build time over that period.',
  },
  {
    route: '/home/build-cost',
    target: '[data-tour="cost-export"]',
    placement: 'auto',
    disableBeacon: true,
    title: 'Export duration data',
    content: 'Download the per-app build-duration breakdown as CSV. The banner above shows month-to-date pipeline time against your monthly build-time budget.',
  },

  // ---- Error Logs ----
  {
    route: '/home/error-logs',
    target: '[data-tour="errors-search"]',
    placement: 'auto',
    disableBeacon: true,
    title: 'Search errors',
    content: 'Find failures by error code, app, or pipeline phase, and narrow by date range to triage what needs attention.',
  },
  {
    route: '/home/error-logs',
    target: '[data-tour="errors-export"]',
    placement: 'auto',
    disableBeacon: true,
    title: 'Export the error log',
    content: 'Download the filtered failures as CSV for sharing or deeper analysis.',
  },

  // ---- Release Notes ----
  {
    route: '/home/release-notes',
    target: '[data-tour="new-release"]',
    placement: 'auto',
    disableBeacon: true,
    title: 'Author release notes',
    content: 'Start a new release note tied to a version. Save it as a draft as you write, then publish when it’s ready.',
  },

  // ---- Audit Log ----
  {
    route: '/home/audit',
    target: '[data-tour="audit-filter"]',
    placement: 'auto',
    disableBeacon: true,
    title: 'Filter the audit trail',
    content: 'Every operator action — deploys, retries, cancels, schedules, publishes — is recorded here. Filter by actor, action, target, or date.',
  },
  {
    route: '/home/audit',
    target: '[data-tour="audit-export"]',
    placement: 'auto',
    disableBeacon: true,
    title: 'Export audit rows',
    content: 'Download the filtered audit entries as CSV — handy for compliance or incident review.',
  },

  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'You’re all set 🎉',
    content: 'That’s the whole dashboard. Replay this tour anytime from the Help menu or ⌘/Ctrl+K → “Replay onboarding tour”. Click Finish to dive in.',
  },
];

interface OnboardingTourProps {
  /** When true the tour runs; the parent flips this off via onClose. */
  active: boolean;
  /** Called when the tour finishes or is skipped — the parent persists completion. */
  onClose: (didFinish: boolean) => void;
}

// An element counts as "ready" only when it's connected and no ancestor is
// hidden. We walk computed styles (rather than offsetParent) so the same check
// is correct in the browser and under jsdom — and crucially it treats a control
// inside a display:none page wrapper (the not-yet-navigated pages) as not ready.
function isElementReady(el: HTMLElement): boolean {
  if (!el.isConnected) return false;
  let node: HTMLElement | null = el;
  while (node) {
    const cs = window.getComputedStyle(node);
    if (cs.display === 'none' || cs.visibility === 'hidden') return false;
    node = node.parentElement;
  }
  return true;
}

// How long to keep polling for a step's target before giving up and skipping
// it (so a slow/heavy page can never freeze the whole tour).
const TARGET_WAIT_MS = 4000;
// Settle time after the router swaps pages before we start looking for the target.
const ROUTE_SWAP_MS = 80;

export const OnboardingTour: React.FC<OnboardingTourProps> = ({ active, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  // Always-fresh current pathname for the (closure-captured) wait loop.
  const pathRef = useRef(location.pathname);
  useEffect(() => { pathRef.current = location.pathname; }, [location.pathname]);

  // Pending async work (rAF / timeouts) + a cancel token so a stale wait loop
  // from a previous step/run can't fire after we've moved on or torn down.
  const rafRef = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);
  const tokenRef = useRef(0);

  const clearPending = () => {
    tokenRef.current += 1; // invalidate any in-flight wait loop
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
    rafRef.current = null;
    timerRef.current = null;
  };

  const finish = (didFinish: boolean) => {
    clearPending();
    setRun(false);
    setStepIndex(0);
    void markOnboardingComplete();
    onClose(didFinish);
  };

  // Show a step only once its target is actually in the DOM, visible, and
  // scrolled into view — this is what kills the "tooltip appears in the wrong
  // place then jumps" glitch and stops the tour from getting stuck on a
  // slow-loading page (e.g. Build Analytics). On timeout we skip the step in
  // the current direction rather than freezing.
  const showStep = (index: number, dir: 1 | -1) => {
    const step = TOUR_STEPS[index];
    if (!step) return finish(true);

    clearPending();
    setRun(false);
    const token = tokenRef.current;
    const start = performance.now();

    const reveal = () => {
      if (token !== tokenRef.current) return;
      setStepIndex(index);
      setRun(true);
    };

    const poll = () => {
      if (token !== tokenRef.current) return;
      const target = step.target as string;

      if (!target || target === 'body') return reveal();

      const el = document.querySelector(target) as HTMLElement | null;
      if (el && isElementReady(el)) {
        // Pre-scroll ourselves (Joyride scrolling is disabled) so the element
        // is in its final position before the tooltip measures + paints.
        try { el.scrollIntoView({ block: 'center', inline: 'nearest' }); } catch { /* noop */ }
        // Two frames so the scroll + layout flush before reveal.
        rafRef.current = requestAnimationFrame(() => {
          if (token !== tokenRef.current) return;
          rafRef.current = requestAnimationFrame(reveal);
        });
        return;
      }

      if (performance.now() - start > TARGET_WAIT_MS) {
        // Target never showed — skip it so the tour keeps moving.
        const next = index + dir;
        return TOUR_STEPS[next] ? showStep(next, dir) : finish(true);
      }
      rafRef.current = requestAnimationFrame(poll);
    };

    if (step.route && step.route !== pathRef.current) {
      navigate(step.route);
      timerRef.current = window.setTimeout(poll, ROUTE_SWAP_MS);
    } else {
      poll();
    }
  };

  // Start / stop the tour when `active` flips.
  useEffect(() => {
    if (!active) {
      clearPending();
      setRun(false);
      return;
    }
    showStep(0, 1);
    return () => clearPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const handleCallback = (data: CallBackProps) => {
    const { action, index, status, type } = data;

    if (status === STATUS.FINISHED) return finish(true);
    if (status === STATUS.SKIPPED || action === ACTIONS.CLOSE) return finish(false);

    // Advance / retreat when a step closes (or its target wasn't found).
    // showStep waits for the next target before revealing it.
    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      const dir: 1 | -1 = action === ACTIONS.PREV ? -1 : 1;
      showStep(index + dir, dir);
    }
  };

  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary?.main || '#8b5cf6';
  const gradient = `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`;

  return (
    <>
      {/* Keyframes for the pulsing spotlight ring + a gentle tooltip entrance.
          Joyride/react-floater render outside our subtree, so we target their
          stable class names globally. */}
      <style>{`
        .react-joyride__spotlight {
          animation: fsamSpotlightPulse 1.8s ease-in-out infinite;
        }
        @keyframes fsamSpotlightPulse {
          0%, 100% { box-shadow: 0 0 0 3px ${primary}66, 0 0 18px 4px ${primary}55; }
          50%      { box-shadow: 0 0 0 6px ${secondary}88, 0 0 30px 8px ${secondary}66; }
        }
        /* IMPORTANT: react-floater positions the .__floater wrapper with an
           inline transform: translate3d(...). Animating "transform" here would
           clobber that and make the tooltip flash at the wrong spot before
           snapping to the target. So we ONLY fade opacity on the wrapper, and
           put the scale/pop on the inner tooltip element (which is NOT
           transform-positioned). */
        .__floater__open { animation: fsamFloaterFade 200ms ease-out; }
        @keyframes fsamFloaterFade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .react-joyride__tooltip { animation: fsamTooltipPop 240ms cubic-bezier(0.16, 1, 0.3, 1); }
        @keyframes fsamTooltipPop {
          from { opacity: 0; transform: scale(0.96); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>

      <Joyride
        steps={TOUR_STEPS}
        run={run}
        stepIndex={stepIndex}
        continuous
        showProgress
        showSkipButton
        disableOverlayClose
        disableScrolling
        spotlightPadding={8}
        callback={handleCallback}
        locale={{ back: 'Back', close: 'Close', last: 'Finish 🎉', next: 'Next →', skip: 'Skip tour' }}
        floaterProps={{ disableAnimation: true, hideArrow: false }}
        styles={{
          options: {
            primaryColor: primary,
            backgroundColor: theme.palette.background.paper,
            arrowColor: theme.palette.background.paper,
            textColor: theme.palette.text.primary,
            overlayColor: 'rgba(15, 23, 42, 0.6)',
            spotlightShadow: `0 0 0 3px ${primary}66`,
            width: 400,
            zIndex: 13000,
          },
          // Constrain width so the tip never runs off a narrow viewport; the
          // `auto` placement on each step lets the floater flip to fit.
          tooltip: {
            borderRadius: 18,
            padding: 0,
            maxWidth: '92vw',
            boxShadow: '0 18px 50px rgba(2, 6, 23, 0.32)',
            overflow: 'hidden',
          },
          tooltipContainer: { textAlign: 'left' },
          tooltipTitle: {
            margin: 0,
            padding: '16px 20px 8px',
            fontSize: 18,
            fontWeight: 800,
            letterSpacing: '-0.01em',
            color: primary,
          },
          tooltipContent: {
            padding: '0 20px 16px',
            fontSize: 14,
            lineHeight: 1.55,
            color: theme.palette.text.secondary,
          },
          tooltipFooter: { margin: 0, padding: '0 16px 14px' },
          buttonNext: {
            background: gradient,
            borderRadius: 12,
            padding: '9px 18px',
            fontSize: 14,
            fontWeight: 700,
            color: '#fff',
            boxShadow: `0 6px 18px ${primary}55`,
            outline: 'none',
          },
          buttonBack: {
            color: primary,
            fontSize: 14,
            fontWeight: 600,
            marginRight: 10,
          },
          buttonSkip: {
            color: theme.palette.text.disabled,
            fontSize: 13,
            fontWeight: 600,
          },
          beaconInner: { backgroundColor: primary },
          beaconOuter: { backgroundColor: `${primary}33`, borderColor: primary },
        }}
      />
    </>
  );
};

export default OnboardingTour;
